import React, { FC } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Box, Text, Flex, Checkbox, Link } from '@resideo/blueprint-react';
import { PRIVACY_POLICY_URL } from 'config';
import { UserTypeEnum } from 'types/enums/User';
import Links from './Links/Links';
import DisabledLinks from './Links/DisabledLinks';
import { UserTerms } from './Links/UserTerms';
import { DisabledLink } from './Links/DisabledLink';
import { StyledCheckboxWrapper, TermsContainer } from './SignUpForm.styles';
import { isMiddleEastCountry } from 'utils/cultureUtils';
import { createMessageHandler } from 'utils/createMessageHandler';

interface TermsSectionProps {
  isCountrySelected: boolean;
  termsAccepted?: boolean;
  onTermsChange: (checked: boolean) => void;
  onFieldTouched: (field: string) => void;
  userType: UserTypeEnum | undefined;
  country?: string;
  proTermsUrl: string;
  consumerTermsUrl: string;
  consumerEulaUrl: string;
  privacyPolicyUrl: string;
}

export const TermsSection: FC<TermsSectionProps> = ({
  isCountrySelected,
  termsAccepted,
  onTermsChange,
  onFieldTouched,
  userType,
  country,
  proTermsUrl,
  consumerTermsUrl,
  consumerEulaUrl,
  privacyPolicyUrl,
}) => {
  const { t } = useTranslation();
  const messageHandler = createMessageHandler();

  return (
    <>
      <Box
        flex={1}
        marginBottom='large'
        data-test-terms
        className='common-input'>
        <TermsContainer data-test-terms-container>
          <StyledCheckboxWrapper>
            <Checkbox
              disabled={!isCountrySelected}
              type='checkbox'
              name='termsAccepted'
              required
              onChange={e => onTermsChange(e.target.checked)}
              checked={termsAccepted}
            />
          </StyledCheckboxWrapper>
          <Flex flexDirection='column'>
            {isCountrySelected ? (
              <>
                {userType === UserTypeEnum.PARTNER && (
                  <Links
                    userType={UserTypeEnum.PARTNER}
                    viewTermsUrl={proTermsUrl}
                    eulaUrl={undefined}
                    privacyPolicy={privacyPolicyUrl}
                    middleEasternCountry={isMiddleEastCountry(country)}
                    country={country}
                  />
                )}
                {userType === UserTypeEnum.CONSUMER && (
                  <Links
                    userType={UserTypeEnum.CONSUMER}
                    viewTermsUrl={consumerTermsUrl}
                    eulaUrl={consumerEulaUrl}
                    privacyPolicy={privacyPolicyUrl}
                    middleEasternCountry={isMiddleEastCountry(country)}
                    country={country}
                  />
                )}
                {userType === UserTypeEnum.DISTRIBUTOR && (
                  <Links
                    userType={UserTypeEnum.DISTRIBUTOR}
                    viewTermsUrl={proTermsUrl}
                    eulaUrl={undefined}
                    privacyPolicy={privacyPolicyUrl}
                    middleEasternCountry={isMiddleEastCountry(country)}
                    country={country}
                    showPerks={false}
                  />
                )}
              </>
            ) : (
              <UserTerms>
                <Text data-test-terms-conditions-label>
                  {t('signUp.acceptTerms')}
                </Text>
                {userType === UserTypeEnum.PARTNER && (
                  <DisabledLinks
                    userType={UserTypeEnum.PARTNER}
                    setFieldTouched={onFieldTouched}
                  />
                )}
                {userType === UserTypeEnum.CONSUMER && (
                  <DisabledLinks
                    userType={UserTypeEnum.CONSUMER}
                    setFieldTouched={onFieldTouched}
                  />
                )}
                {userType === UserTypeEnum.DISTRIBUTOR && (
                  <DisabledLinks
                    userType={UserTypeEnum.DISTRIBUTOR}
                    setFieldTouched={onFieldTouched}
                  />
                )}
              </UserTerms>
            )}
          </Flex>
        </TermsContainer>
      </Box>

      {!isMiddleEastCountry(country) && (
        <Box data-test-privacy-policy className='common-input'>
          <Text>
            <Trans
              i18nKey='signUp.acceptPrivacyPolicy'
              components={
                isCountrySelected
                  ? [
                      <Link
                        data-test-privacy-policy-link
                        key={0}
                        href={
                          !messageHandler.hasMessageHandler
                            ? PRIVACY_POLICY_URL
                            : '#'
                        }
                        target='_blank'
                        onClick={e => {
                          if (messageHandler.hasMessageHandler) {
                            e.preventDefault();
                            messageHandler.postMessage(
                              'openExternalUrl',
                              PRIVACY_POLICY_URL
                            );
                          }
                        }}
                      />,
                    ]
                  : [
                      <DisabledLink
                        data-test-privacy-policy-link
                        disabled
                        key={0}
                        target='_blank'
                        onClick={() => onFieldTouched('country')}
                      />,
                    ]
              }
            />
          </Text>
        </Box>
      )}
    </>
  );
};
